(ns app.front.view.group.admin.config.counter-config-editor
  "An editor for arbitrary numbering schemes, adapted from permit/licence numbering but without inheratance support
   for the configuration tree."
  (:require [app.domain.counter-config.model :as counter-config-model]
            [app.domain.group.number-format-utils :as nf-utils]
            [app.front.entities :as entities]
            [app.front.ui.error-boundary :as eb]
            [app.front.util :as futil]
            [app.front.view.group.admin.config.number-format-editor :as nf-editor]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn- current-counter [{:keys [current-year entity]}]
  (some->> entity
           :counter-state/_counter-config
           (filter #(if (counter-config-model/template-has-year? (:counter-config/template entity))
                      (-> % :counter-state/year (= current-year))
                      (-> % :counter-state/year nil?)))
           first
           :counter-state/counter))


(defn counter-value [{:keys [entity-id entity current-year]}]
  (inc (or (<sub [::updated-value entity-id :counter-config/initial-counter])
           (current-counter (util/mmap current-year entity))
           0)))

(defn default-values [type]
  {:counter-config/type     type
   :counter-config/template (or (nf-utils/config-type-specific-default-templates type)
                                nf-utils/default-template-str)})

(rf/reg-sub ::with-unsaved-changes
  (fn [[_ entity-id]]
    [(nf-editor/safe-entity-sub entity-id)
     (rf/subscribe [::nf-editor/unsaved-changes entity-id])])
  (fn [[entity changes] [_ entity-id]]
    (merge (default-values entity-id)
           (into {} entity)
           changes)))

(rf/reg-sub ::updated-value
  (fn [[_ entity-id _attr]]
    [(rf/subscribe [::with-unsaved-changes entity-id])])
  (fn [[entity] [_ _ attr]]
    (when attr
      (attr entity))))

(rf/reg-sub ::save-content
  (fn [[_ config-id _]]
    (rf/subscribe [::nf-editor/unsaved-changes config-id]))
  (fn [changes [_ _ config-type]]
    (-> changes
        (assoc :counter-config/type config-type))))

(rf/reg-event-fx ::set-counter-config
  (fn [_ [_ entity-id group-id new-config]]
    {:comm/command {:command    :group/set-counter-config
                    :data       (merge new-config
                                       {:counter-config/parent group-id})
                    :notify     {:success :counter-config/save-success}
                    :on-success [::set-counter-config.success entity-id]}}))

(rf/reg-event-fx ::set-counter-config.success
  (fn [_ [_ entity-id status response]]
    {:dispatch-n [[::entities/apply-changes status response]
                  [::nf-editor/undo-changes entity-id]]}))

(rf/reg-sub ::has-template-var?
  (fn [[_ entity-id]]
    (rf/subscribe [::updated-value entity-id :counter-config/template]))
  (fn [template [_ _ var]]
    (str/includes? template var)))

(defn- template-errors
  "Returns a vector of strings describing the problem if the current counter config applied with changes is invalid."
  [{:keys [entity-id tr] :as ctx}]
  (let [with-unsaved-changes (<sub [::with-unsaved-changes entity-id])
        config               (-> with-unsaved-changes
                                 (p/assoc-if :min-value (current-counter ctx)))]
    (counter-config-model/validation-error-texts tr config)))

(defn- component-input
  [{:keys                                 [type attr class fallback-value]
    {:keys [tr entity entity-id on-edit]} :ctx
    :as                                   opts}]
  (let [attr-name (-> attr name (str/replace #"\?" ""))
        attr-tr   #(tr [(keyword "counter-config" (str attr-name %))])
        tval-fn   (case type
                    :text (comp str-util/blank->nil futil/tval)
                    :number (comp futil/parse-int futil/tval)
                    :boolean identity)]
    [nf-editor/component-input-impl
     (util/mmap {:opts (dissoc opts :ctx :type :attr :class :fallback-value)}
                type entity-id attr fallback-value on-edit class attr-tr entity tval-fn)]))


(defn- sortable-components
  [{:keys [tr entity entity-id on-edit custom-data-tr-key]}]
  [nf-editor/sortable-components-impl
   (util/mmap {:template-legend-tr-key :counter-config.escrow-account/template
               :updated-value          (<sub [::updated-value entity-id :counter-config/template])
               :template-attr          :counter-config/template}
              on-edit entity entity-id tr custom-data-tr-key)])


(defn- editor
  "The editor proper, featuring the re-arrangeable template components and the fields for inputting their values"
  [{:keys [tr entity entity-id on-edit counter-config-type custom-data-tr-key] :as ctx}]
  (let [on-change (fn [attr value]
                    (rf/dispatch-sync [::nf-editor/edit-value (or entity {:db/id entity-id}) attr value]))]
    [:div.flex-col
     [sortable-components (util/mmap tr entity entity-id on-edit counter-config-type custom-data-tr-key)]
     [:div.flex.flex-wrap.items-end.gap-y-2.gap-x-4
      (when (<sub [::has-template-var? entity-id "P"])
        [component-input {:ctx ctx :type :text :attr :counter-config/prefix}])
      (when (<sub [::has-template-var? entity-id "S"])
        [component-input {:ctx ctx :type :text :attr :counter-config/separator}])
      (when (counter-config-model/initial-counter-editable? counter-config-type)
        [component-input {:ctx       ctx
                          :type      :number
                          :attr      :counter-config/initial-counter
                          :min       (or (inc (current-counter ctx)) 1)
                          :value     (counter-value ctx)
                          :on-change #(on-change :counter-config/initial-counter (-> % futil/tval futil/parse-int dec))}])
      [component-input
       (p/assoc-if {:ctx  ctx
                    :type :number
                    :attr :counter-config/digits
                    :max  10}
                   :value nil)]]]))

(defn- number-preview
  "A preview of the next number generated according to the current (unsaved) format.
  If there's no template active, then display the default (same as workspace-id) preview"
  [{:keys [tr entity-id entity current-year counter-config-type]}]
  [nf-editor/number-preview-box
   {:label (tr [(keyword (str "counter-config." (name counter-config-type)) "sample-lbl") :counter-config.escrow-account/sample-lbl])
    :value (nf-editor/generate-number-preview
             (util/mmap {:counter-value  (counter-value (util/mmap entity-id entity current-year))
                         :template-attr  :counter-config/template
                         :digits-attr    :counter-config/digits
                         :prefix-attr    :counter-config/prefix
                         :separator-attr :counter-config/separator
                         :get-value      (fn [attr default]
                                           (or (<sub [::updated-value entity-id attr])
                                               default))}
                        current-year tr))}])

(defn counter-config-editor
  [{:keys [tr group-id counter-config-type custom-data-tr-key]}]
  (let [group          (<sub [::entities/entity group-id])
        counter-config (<sub [::entities/entity {:counter-config/parent+type [group-id counter-config-type]}])
        entity-id      (or (:db/id counter-config) counter-config-type) ;; use type as "entity-id" to support multiple editor instances
        current-year   (nf-editor/effective-counter-year (util/mmap {:rollover-month nil ;; TODO
                                                                     :rollover-day   nil}
                                                                    group))
        errors         (template-errors (util/mmap {:entity counter-config} entity-id tr current-year))
        new-config     (<sub [::save-content entity-id counter-config-type])]
    [eb/error-boundary
     [nf-editor/info-box-1 (tr [:counter-config/template-info])]
     [editor (util/mmap {:entity counter-config}
                        tr group counter-config-type current-year entity-id custom-data-tr-key)]
     [number-preview (util/mmap {:entity counter-config}
                                tr counter-config-type entity-id current-year)]
     (when (seq errors)
       (futil/for-frag [error-text errors]
         [nf-editor/error-box (util/mmap error-text)]))
     [nf-editor/save-cancel-buttons
      (util/mmap {:save-disabled? (seq errors)
                  :on-save        #(>evt [::set-counter-config entity-id group-id new-config])}
                 tr entity-id)]]))
